<template>
  <a-form layout="vertical" @submit.prevent="$emit('submit', value)">
    <p class="text-muted mb-3">
      Choose a lookback from {{ lookbackDescription }} for each analysis.
    </p>

    <a-row :gutter="24">
      <a-col :xs="24" :md="12">
        <a-form-item label="Frequency">
          <a-radio-group v-model="frequencyModel" size="large">
            <a-radio-button value="daily">
              Daily
            </a-radio-button>
            <a-radio-button value="weekly">
              Weekly
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
      </a-col>
    </a-row>

    <a-row :gutter="24">
      <a-col v-if="frequencyModel === 'weekly'" :xs="24" :md="8">
        <a-form-item label="Day of Week">
          <a-select v-model="dayOfWeekModel" size="large" class="full-w">
            <a-select-option
              v-for="day in weekDayOptions"
              :key="day.value"
              :value="day.value"
            >
              {{ day.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="8">
        <a-form-item label="Time">
          <a-time-picker
            v-model="timeModel"
            format="HH:mm:ss"
            size="large"
            class="full-w"
            placeholder="Select time"
          />
        </a-form-item>
      </a-col>
    </a-row>

    <a-row :gutter="24">
      <a-col :xs="24" :md="8">
        <a-form-item label="Lookback amount">
          <a-input-number
            v-model="lookbackAmountModel"
            :min="lookbackMinimum"
            :max="lookbackMaximum"
            :precision="0"
            size="large"
            class="full-w"
            placeholder="Enter amount"
          />
        </a-form-item>
      </a-col>
      <a-col :xs="24" :md="8">
        <a-form-item label="Lookback unit">
          <a-select
            v-model="lookbackUnitModel"
            size="large"
            class="full-w"
            placeholder="Select unit"
          >
            <a-select-option
              v-for="unit in lookbackUnitOptions"
              :key="unit.value"
              :value="unit.value"
            >
              {{ unit.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-col>
    </a-row>

    <AutoFinetuneThresholdFields
      :metadata="metadata"
      :threshold-alert="thresholdAlertModel"
      :frequency-threshold="frequencyThresholdModel"
      @update:threshold-alert="thresholdAlertModel = $event"
      @update:frequency-threshold="frequencyThresholdModel = $event"
    />

    <AutoFinetuneContactGroupField
      v-model="contactGroupIdModel"
      :contact-groups="contactGroups"
      :loading="loadingContactGroups"
    />

    <div :class="{ 'd-flex justify-content-end mt-3': mode === 'edit' }">
      <a-button v-if="mode === 'edit'" size="large" class="mr-2" @click="$emit('cancel')">
        Cancel
      </a-button>
      <a-button type="primary" size="large" html-type="submit" :loading="saving">
        {{ mode === 'edit' ? 'Save' : 'Create Schedule' }}
      </a-button>
    </div>
  </a-form>
</template>

<script>
import AutoFinetuneContactGroupField from './AutoFinetuneContactGroupField.vue';
import AutoFinetuneThresholdFields from './AutoFinetuneThresholdFields.vue';
import {
  WEEK_DAY_OPTIONS,
  getLookbackUnitLimits
} from '../helpers/recurringSchedule';

export default {
  name: 'AutoFinetuneRecurringScheduleForm',
  components: {
    AutoFinetuneContactGroupField,
    AutoFinetuneThresholdFields
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    metadata: {
      type: Object,
      required: true
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value)
    },
    saving: {
      type: Boolean,
      default: false
    },
    contactGroups: {
      type: Array,
      default: () => []
    },
    loadingContactGroups: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      weekDayOptions: WEEK_DAY_OPTIONS
    };
  },
  computed: {
    frequencyModel: {
      get() {
        return this.value.frequency;
      },
      set(value) {
        this.updateDraft('frequency', value);
      }
    },
    dayOfWeekModel: {
      get() {
        return this.value.dayOfWeek;
      },
      set(value) {
        this.updateDraft('dayOfWeek', value);
      }
    },
    timeModel: {
      get() {
        return this.value.time;
      },
      set(value) {
        this.updateDraft('time', value);
      }
    },
    lookbackAmountModel: {
      get() {
        return this.value.lookbackAmount;
      },
      set(value) {
        this.updateDraft('lookbackAmount', value);
      }
    },
    lookbackUnitModel: {
      get() {
        return this.value.lookbackUnit;
      },
      set(value) {
        this.updateDraft('lookbackUnit', value);
      }
    },
    thresholdAlertModel: {
      get() {
        return this.value.thresholdAlert;
      },
      set(value) {
        this.updateDraft('thresholdAlert', value);
      }
    },
    frequencyThresholdModel: {
      get() {
        return this.value.frequencyThreshold;
      },
      set(value) {
        this.updateDraft('frequencyThreshold', value);
      }
    },
    contactGroupIdModel: {
      get() {
        return this.value.contactGroupId;
      },
      set(value) {
        this.updateDraft('contactGroupId', value);
      }
    },
    lookbackLimits() {
      return (
        getLookbackUnitLimits(
          this.metadata,
          this.value.lookbackUnit || 'hours'
        ) || {}
      );
    },
    lookbackUnitOptions() {
      const lookback = (this.metadata.limits && this.metadata.limits.lookback) || {};
      return Object.keys(lookback).map((value) => ({
        value,
        label: `${value.charAt(0).toUpperCase()}${value.slice(1)}`
      }));
    },
    lookbackMinimum() {
      return this.lookbackLimits.min != null
        ? Number(this.lookbackLimits.min)
        : undefined;
    },
    lookbackMaximum() {
      return this.lookbackLimits.max != null
        ? Number(this.lookbackLimits.max)
        : null;
    },
    lookbackDescription() {
      return this.lookbackUnitOptions
        .map((unit) => {
          const limits = getLookbackUnitLimits(this.metadata, unit.value) || {};
          return `${limits.min}–${limits.max} ${unit.label.toLowerCase()}`;
        })
        .join(' or ');
    }
  },
  methods: {
    updateDraft(key, value) {
      this.$emit('input', {
        ...this.value,
        [key]: value
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.full-w {
  width: 100%;
}
</style>
